import React, { ReactNode, useContext, useMemo, useState } from 'react';
import { DownloadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import CsvExport from '@components/CsvExport';
import FileUploader from '@components/FileUploader';
import { Button, Space, Tooltip, Table, UploadProps } from 'antd';
import Text from 'antd/lib/typography/Text';
import { useTranslation } from 'react-i18next';
import CustomModalInfo from '@components/CustomModalInfo';
import SessionContext from '@providers/Session/SessionContext';
import rolesConstants from '@utils/rolesConstants';
import { getUploadModels } from './UploadModels';

interface ActionsUploadProps extends UploadProps {
  onUpload: (devices: { [key: string]: string }[] | undefined) => void;
  actionKey?: number | undefined;
  columnsNames?: string[];
  onRemove?: () => void;
  csvFilename?: string;
  customInfo?: ReactNode;
  disabled?: boolean;
  isLoraWan?: boolean;
}

const ActionsUpload = ({
  onUpload,
  actionKey,
  onRemove,
  csvFilename,
  customInfo,
  disabled,
  isLoraWan,
  ...props
}: ActionsUploadProps) => {
  const { t } = useTranslation('actions-modal');
  const { hasPermission, isMotBusiness } = useContext(SessionContext);
  const hasNewLora = hasPermission(rolesConstants.LORAWAN_NEW);
  const [uploadedFile, setUploadedFile] = useState();

  const [openModalInfo, setOpenModalInfo] = useState(false);

  const uploadModels = useMemo(
    () =>
      getUploadModels({
        t,
        actionKey,
        columnsNames: props?.columnsNames,
        isLoraWan,
        isMotBusiness,
        hasNewLora,
      }),
    [t, actionKey, props?.columnsNames, isLoraWan, isMotBusiness, hasNewLora],
  );

  return (
    <>
      <CustomModalInfo
        title={t('upload.csv-format-example')}
        open={openModalInfo}
        width={700}
        onClose={() => setOpenModalInfo(false)}
      >
        <Table
          bordered
          tableLayout="auto"
          size="small"
          columns={uploadModels?.columns}
          pagination={false}
          dataSource={uploadModels?.data}
          style={{ width: '100%' }}
        />
        <Space
          style={{
            margin: '16px 0',
            display: 'flex',
            justifyContent: 'end',
          }}
        >
          <CsvExport
            data={uploadModels?.data}
            filename={`${csvFilename ?? 'import-model'}.csv`}
            columns={uploadModels?.columns}
            separator=","
          >
            <Button icon={<DownloadOutlined />} type="primary">
              {t('upload.download-model')}
            </Button>
          </CsvExport>
        </Space>
        {customInfo}
      </CustomModalInfo>
      <Space style={{ marginBottom: 16 }}>
        <Text>
          {t('upload.select-devices')}
          <Tooltip title={t('upload.help')} placement="right">
            <Button
              type="link"
              size="small"
              icon={<QuestionCircleOutlined />}
              onClick={() => setOpenModalInfo(true)}
              disabled={disabled}
            />
          </Tooltip>
        </Text>
      </Space>
      <FileUploader
        onUpload={onUpload}
        uploadColumns={uploadModels?.columns}
        onRemove={onRemove}
        disabled={disabled}
        filesListState={[uploadedFile, setUploadedFile]}
        {...props}
      />
    </>
  );
};

export default ActionsUpload;
